import { useEffect } from "react";
import { useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  systemMessageFormSchema,
  type LocalizedText,
  type SystemMessageFormData,
  type SystemMessage,
  type SystemMessagePayload
} from "@/models/system-message";
import { useCreateSystemMessage } from "../hooks/useCreateSystemMessage";
import { useUpdateSystemMessage } from "../hooks/useUpdateSystemMessage";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import {
  DISPLAY_TYPE_LABELS,
  DISPLAY_TYPE_ORDER,
  TYPE_LABELS,
  TYPE_ORDER
} from "../system-messages.meta";
import { OrganizationPicker } from "./OrganizationPicker";

interface SystemMessageFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemMessage?: SystemMessage | null;
}

const EMPTY_TEXT = { en: "", ar: "" };

const DEFAULT_VALUES: SystemMessageFormData = {
  title: EMPTY_TEXT,
  body: EMPTY_TEXT,
  type: "announcement",
  displayType: "banner",
  dismissible: true,
  enabled: true,
  startAt: "",
  endAt: "",
  linkUrl: "",
  linkText: EMPTY_TEXT,
  languages: "en",
  audience: "all",
  organizationIds: []
};

// datetime-local inputs use "YYYY-MM-DDTHH:mm" in local time, unlike the
// ISO 8601 UTC strings the API stores — these two convert between them.
const toDatetimeLocal = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (value?: string): string | null =>
  value ? new Date(value).toISOString() : null;

const toFormText = (text?: LocalizedText | null) => ({ en: text?.en ?? "", ar: text?.ar ?? "" });

type Languages = SystemMessageFormData["languages"];

const LANGUAGE_OPTIONS: { value: Languages; label: string }[] = [
  { value: "en", label: "English" },
  { value: "ar", label: "العربية" },
  { value: "both", label: "Both" }
];

const languagesOf = (body: LocalizedText): Languages => {
  const en = !!body.en?.trim();
  const ar = !!body.ar?.trim();
  if (en && ar) return "both";
  return ar ? "ar" : "en";
};

// Drops whatever was typed in a language that is no longer selected.
const onlyLanguages = (text: LocalizedText, languages: Languages): LocalizedText => ({
  ...(languages !== "ar" && { en: text.en }),
  ...(languages !== "en" && { ar: text.ar })
});

// null clears the field on update; the backend does the same trimming.
const toPayloadText = (text: LocalizedText): LocalizedText | null => {
  const en = text.en?.trim();
  const ar = text.ar?.trim();
  return en || ar ? { ...(en && { en }), ...(ar && { ar }) } : null;
};

interface BilingualFieldProps {
  control: Control<SystemMessageFormData>;
  name: "title" | "body" | "linkText";
  label: string;
  placeholder: { en: string; ar: string };
  languages: Languages;
  multiline?: boolean;
}

function BilingualField({
  control,
  name,
  label,
  placeholder,
  languages,
  multiline
}: BilingualFieldProps) {
  const Field = multiline ? Textarea : Input;
  const both = languages === "both";
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className={both ? "grid grid-cols-1 gap-3 md:grid-cols-2" : "grid grid-cols-1"}>
        {languages !== "ar" && (
          <FormField
            control={control}
            name={`${name}.en`}
            render={({ field }) => (
              <FormItem className="space-y-1">
                {both && (
                  <FormLabel className="text-xs font-normal text-muted-foreground">
                    English
                  </FormLabel>
                )}
                <FormControl>
                  <Field
                    placeholder={placeholder.en}
                    className={multiline ? "min-h-[90px]" : undefined}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {languages !== "en" && (
          <FormField
            control={control}
            name={`${name}.ar`}
            render={({ field }) => (
              <FormItem className="space-y-1">
                {both && (
                  <FormLabel className="text-xs font-normal text-muted-foreground">
                    العربية
                  </FormLabel>
                )}
                <FormControl>
                  <Field
                    dir="rtl"
                    lang="ar"
                    placeholder={placeholder.ar}
                    className={multiline ? "min-h-[90px]" : undefined}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}

export function SystemMessageFormDialog({
  open,
  onOpenChange,
  systemMessage
}: SystemMessageFormDialogProps) {
  const isEditing = !!systemMessage;
  const { mutate: createSystemMessage, isPending: isCreating } = useCreateSystemMessage();
  const { mutate: updateSystemMessage, isPending: isUpdating } = useUpdateSystemMessage();
  const isPending = isCreating || isUpdating;

  const form = useForm<SystemMessageFormData>({
    resolver: zodResolver(systemMessageFormSchema),
    defaultValues: DEFAULT_VALUES
  });
  const audience = form.watch("audience");
  const languages = form.watch("languages");

  // Re-sync the form whenever a different message is opened for editing (or
  // the dialog is reopened for a fresh create).
  useEffect(() => {
    if (!open) return;
    if (systemMessage) {
      const organizationIds = (systemMessage.organizationIds ?? []).map((org) => org._id);
      form.reset({
        title: toFormText(systemMessage.title),
        body: toFormText(systemMessage.body),
        type: systemMessage.type,
        displayType: systemMessage.displayType,
        dismissible: systemMessage.dismissible,
        enabled: systemMessage.enabled,
        startAt: toDatetimeLocal(systemMessage.startAt),
        endAt: toDatetimeLocal(systemMessage.endAt),
        linkUrl: systemMessage.linkUrl ?? "",
        linkText: toFormText(systemMessage.linkText),
        languages: languagesOf(systemMessage.body),
        audience: organizationIds.length ? "specific" : "all",
        organizationIds
      });
    } else {
      form.reset(DEFAULT_VALUES);
    }
  }, [open, systemMessage]);

  const onSubmit = (data: SystemMessageFormData) => {
    const payload: SystemMessagePayload = {
      title: toPayloadText(onlyLanguages(data.title, data.languages)),
      body: toPayloadText(onlyLanguages(data.body, data.languages)) ?? {},
      type: data.type,
      displayType: data.displayType,
      dismissible: data.dismissible,
      enabled: data.enabled,
      startAt: fromDatetimeLocal(data.startAt),
      endAt: fromDatetimeLocal(data.endAt),
      linkUrl: data.linkUrl || null,
      linkText: toPayloadText(onlyLanguages(data.linkText, data.languages)),
      organizationIds: data.audience === "all" ? [] : data.organizationIds
    };

    const onSuccess = () => onOpenChange(false);

    if (isEditing) {
      updateSystemMessage({ id: systemMessage._id, payload }, { onSuccess });
    } else {
      createSystemMessage(payload, { onSuccess });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit System Message" : "New System Message"}</DialogTitle>
          <DialogDescription>Broadcast a banner and/or popup to dashboard users.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TYPE_ORDER.map((value) => (
                          <SelectItem key={value} value={value}>
                            {TYPE_LABELS[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display as</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DISPLAY_TYPE_ORDER.map((value) => (
                          <SelectItem key={value} value={value}>
                            {DISPLAY_TYPE_LABELS[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="audience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Audience</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-wrap gap-6"
                    >
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="all" />
                        All organizations
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="specific" />
                        Specific organizations
                      </label>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            {audience === "specific" && (
              <FormField
                control={form.control}
                name="organizationIds"
                render={({ field }) => (
                  <FormItem>
                    <OrganizationPicker value={field.value} onChange={field.onChange} />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="languages"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message language</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-wrap gap-6"
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <RadioGroupItem value={option.value} />
                          {option.label}
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    {languages === "both"
                      ? "Users see the version matching their dashboard language."
                      : "Everyone sees this language, whatever their dashboard language is."}
                  </FormDescription>
                </FormItem>
              )}
            />

            <BilingualField
              control={form.control}
              languages={languages}
              name="title"
              label="Title (optional)"
              placeholder={{ en: "Scheduled maintenance", ar: "صيانة مجدولة" }}
            />

            <BilingualField
              control={form.control}
              languages={languages}
              name="body"
              label="Message *"
              placeholder={{
                en: "We'll be performing scheduled maintenance on...",
                ar: "سنقوم بإجراء صيانة مجدولة في..."
              }}
              multiline
            />

            <FormField
              control={form.control}
              name="linkUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link URL (optional)</FormLabel>
                  <FormControl>
                    <Input type="url" placeholder="https://status.enigma-ai.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <BilingualField
              control={form.control}
              languages={languages}
              name="linkText"
              label="Link text (optional)"
              placeholder={{ en: "Learn more", ar: "معرفة المزيد" }}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Starts (optional)</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormDescription>Empty = active immediately.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ends (optional)</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormDescription>Empty = no expiry.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="dismissible"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Dismissible</FormLabel>
                      <FormDescription>Users can close it.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Enabled</FormLabel>
                      <FormDescription>Manual on/off switch.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? "Save changes" : "Create message"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
